// utils/units/parse.js
//
// The tolerant reader of user input for a measurement element: numbers,
// `{ value, unit }` objects and free-text strings ("8 lb 7 oz", "150lb",
// "1.5kg", "-3 C", "5'11\"", "1:30"). Understands, never enforces: an entry
// allowlist is the normaliser's job (normalize.js). Never throws.
//
// A term's number may be a fraction (KPR-1025): "1 1/2 cups", "3/4 in",
// "1-1/2 in", a fraction character (U+00BD tsp), "5 ft 3 1/2 in", and a { value: '1 1/2' } object.
// fraction.js reads it, and refuses a malformed one ("1/0 cup") with a reason
// this file puts in its 'type' sentence. A unit spelled with a slash ("8
// L/100km", "5 m3/h") is not a fraction: a fraction's slash has digits on
// both sides, and no unit starts with a digit.
// Dependency-free and vendored into keepr-web (see registry.js for the rule).

'use strict';

const { getMeasure, getUnit, unitsOf, resolveUnit, allowedMeasures, scopeOptions, isOwnUnitId, ownUnitsOf, ownUnitOf, resolveOwnUnit } = require('./registry');
const Fraction = require('./fraction');

// A decimal number; the first term of a string may carry a sign.
const SIGNED_NUMBER_RE = /^[-+]?(?:\d+\.?\d*|\.\d+)/;
const UNSIGNED_NUMBER_RE = /^(?:\d+\.?\d*|\.\d+)/;
// A unit spelling: everything up to the next digit, decimal point or fraction
// character (KPR-1025: the half of "8 lb \u00bd oz" starts the next term).
const UNIT_TEXT_RE = /^[^\d.\u00bc-\u00be\u2150-\u215e\u2189]+/;
// Clock-style duration: 1:30, 0:45, 12:05.
const CLOCK_RE = /^([-+]?\d+):(\d{1,2})$/;
// A squared or cubed unit that a rate's time follows: the "3/h" of "m3/h",
// the "3 per min" of "ft3 per min". Everything after the separator is unit
// text, as UNIT_TEXT_RE reads it.
const RATE_TAIL_RE = /^[23](?:\s*\/|\s+(?:per|an?)\s)[^\d.]*/i;

// "8 L/100km", "8 l per 100 km": a unit whose divisor is a quantity, so its
// spelling holds digits the tokenizer would read as a second term. The rest of
// the text joins the unit only when the unit text ends in a divisor (a slash,
// or "per") AND the whole joined spelling makes a unit. Nothing else ever
// spelled a unit that way -- "L/" followed by a term was always a refusal --
// so no reading that worked before changes. Bounded, like the rate split
// (registry.js): a stranger's padded input is never looked at.
const MAX_DIVISOR_SPELLING = 40;

function joinDivisor(unitText, rest, measureId) {
	const head = unitText.trim().toLowerCase();
	if (!head || head.length + rest.length > MAX_DIVISOR_SPELLING) return null;
	if (head.charAt(head.length - 1) !== '/' && head.slice(-4) !== ' per') return null;
	const spelled = (unitText + rest).trim();
	const unit = resolveUnit(spelled, measureId)
		|| resolveUnit(spelled.split('/').map(part => part.trim()).join('/'), measureId);
	return unit ? unit.id : null;
}

// `reason`, when given, is the part of the message a caller with its own
// wording keeps (a malformed fraction's "a fraction has one slash ...").
function reject(code, message, reason) {
	return reason ? { error: code, message, reason } : { error: code, message };
}

// The words a refusal names the element's measures in: "volume", or for an
// element that allows several, "volume, mass or count" (KPR-1085). Keyed by
// the options object a reading is made with, so nothing a caller passes can
// set it.
const LABELS = new WeakMap();
// The options objects of the NON-primary measures of a multi-measure element:
// a reading in one of them needs a unit spelled out, because a number with no
// unit is the PRIMARY measure's default unit and no other's.
const NO_DEFAULT = new WeakSet();

function describeMeasure(measure, options) {
	if (options && LABELS.has(options)) return LABELS.get(options);
	return measure ? measure.id : 'measurement';
}

function defaultUnitFor(measure, options) {
	if (options && NO_DEFAULT.has(options)) return null;
	if (options && typeof options.defaultUnit === 'string') {
		const unit = getUnit(options.defaultUnit);
		if (unit && (!measure || unit.measure === measure.id)) return unit;
	}
	return measure ? getUnit(measure.canonical) : null;
}

function allowed(unitId, options) {
	const list = options && Array.isArray(options.units) ? options.units : [];
	return list.length === 0 || list.indexOf(unitId) !== -1;
}

// A term's number, from the start of `rest`: a fraction first ("1 1/2",
// a fraction character; a sign only on the first term), else a decimal. { n, length } |
// { error } (a fraction's shape that is not one) | null (no number here).
function readTermNumber(rest, first) {
	const head = Fraction.fractionHead(rest);
	if (head !== null && (first || !/^[+-]/.test(head))) {
		const fraction = Fraction.readFraction(head);
		if (fraction.error) return { error: fraction.error };
		return { n: fraction.value, length: head.length };
	}
	const numberMatch = rest.match(first ? SIGNED_NUMBER_RE : UNSIGNED_NUMBER_RE);
	if (!numberMatch) return null;
	const n = Number(numberMatch[0]);
	if (!Number.isFinite(n)) return null;
	return { n, length: numberMatch[0].length };
}

// Split "8 lb 7 oz" into [{ n: 8, u: 'lb' }, { n: 7, u: 'oz' }]. null when
// the text is not terms; { error } when a term's fraction is malformed.
function tokenize(text, measureId) {
	let rest = text;
	const terms = [];
	while (rest.length) {
		const number = readTermNumber(rest, terms.length === 0);
		if (!number) return null;
		if (number.error) return number;
		const n = number.n;
		rest = rest.slice(number.length);
		// A slash straight after the number with a digit, another slash or
		// nothing after it ("1//2", "1/2/3 cup", "1/"), or a fraction character
		// straight after a fraction, is a fraction gone wrong, never a unit.
		// A slash before a word ("60/min" on a count rate) is left to the unit
		// reader, which names the unit it cannot read, as it always has.
		if (/^\s*\/\s*(?:[\d/]|$)/.test(rest) || (Fraction.hasFractionMark(rest.charAt(0)) && rest.charAt(0) !== '/')) {
			const reason = Fraction.readFraction(text.slice(0, text.length - rest.length) + rest.replace(/[^\d\s/+\-.,\u00bc-\u00be\u2150-\u215e\u2189].*$/, ''));
			return { error: reason && reason.error ? reason.error : 'a fraction has one slash, like 3/4 or 1 1/2' };
		}
		const unitMatch = rest.match(UNIT_TEXT_RE);
		// A sign before a later term ("1 -1/2 cup", "5 ft -3 in"): no unit is
		// spelled ending in one, and only the first term may carry it.
		if (unitMatch && /[+-]\s*$/.test(unitMatch[0]) && /^\d/.test(rest.slice(unitMatch[0].length))) {
			return { error: 'a sign goes only at the very front, like -1 1/2' };
		}
		let u = unitMatch ? unitMatch[0].trim() : '';
		if (unitMatch) rest = rest.slice(unitMatch[0].length);
		const divided = unitMatch && /^\d/.test(rest) ? joinDivisor(unitMatch[0], rest, measureId) : null;
		if (divided) {
			terms.push({ n, u: divided });
			break;
		}
		// Area and volume ids end in a digit (ft2, m2, km2, yd3, cm3): "5 ft2"
		// and "5 yd3" are one term, but "5 ft 2 in" and "5ft2in" are two. The
		// digit belongs to the unit only when it follows the letters directly,
		// ends the term, and makes a unit -- or when the rest of a RATE follows
		// it ("5 m3/h", "2 ft3 per min") and the whole spelling makes one.
		const exponent = rest.charAt(0);
		const rateTail = unitMatch && !/\s$/.test(unitMatch[0]) ? rest.match(RATE_TAIL_RE) : null;
		if (rateTail && resolveUnit(u + rateTail[0], measureId)) {
			u = (u + rateTail[0]).trim();
			rest = rest.slice(rateTail[0].length);
		} else if (unitMatch && (exponent === '2' || exponent === '3') && !/\s$/.test(unitMatch[0]) && !/^[23][^\s]/.test(rest)
			&& resolveUnit(u + exponent, measureId)) {
			u += exponent;
			rest = rest.slice(1);
		}
		terms.push({ n, u });
		if (terms.length > 2) return null;
	}
	return terms;
}

function findComposite(measure, majorId, minorId) {
	const major = getUnit(majorId);
	const candidates = unitsOf(measure ? measure.id : (major ? major.measure : null));
	for (const unit of candidates) {
		if (unit.parts && unit.parts[0] === majorId && unit.parts[1] === minorId) return unit;
	}
	return null;
}

// Only a thousands separator is dropped ("1,234.5"); a comma anywhere else
// ("1,5" as a decimal, "1,2,3") is left in place to fail, never read as 15.
const GROUPING_COMMA_RE = /,(?=\d{3}(?!\d))/g;

function parseString(raw, measure, options) {
	const text = raw.replace(GROUPING_COMMA_RE, '').trim();
	if (!text) return reject('type', 'a measurement is required');
	if (text.indexOf(',') !== -1) return reject('type', '"' + raw + '" is not a valid ' + describeMeasure(measure, options));
	const measureId = measure ? measure.id : undefined;

	// Clock-style durations resolve to whichever of h-min / min-s the element
	// accepts (h-min first; the allowlist is the tiebreaker).
	const clock = text.match(CLOCK_RE);
	if (clock && measure && measure.id === 'duration') {
		const major = Number(clock[1]);
		const minor = Number(clock[2]);
		if (minor >= 60) return reject('type', '"' + raw + '" is not a valid duration');
		const unit = allowed('h-min', options) ? getUnit('h-min') : (allowed('min-s', options) ? getUnit('min-s') : null);
		if (!unit) return reject('invalid_unit', 'clock-style durations are not allowed for this element');
		const sign = major < 0 || clock[1].charAt(0) === '-' ? -1 : 1;
		return { value: sign * (Math.abs(major) + minor / unit.minorPerMajor), unit: unit.id };
	}

	const terms = tokenize(text, measureId);
	if (terms && terms.error) return reject('type', '"' + raw + '" is not a valid ' + describeMeasure(measure, options) + ': ' + terms.error, terms.error);
	if (!terms || !terms.length) {
		// A fraction mark in text that is not terms at all ("/2 cup"): say
		// what a fraction needs, rather than only that this is not one.
		const fraction = Fraction.hasFractionMark(text) ? Fraction.readFraction(text.replace(/[^\d\s/+\-.,\u00bc-\u00be\u2150-\u215e\u2189\u2044\u2215].*$/, '')) : null;
		const reason = fraction && fraction.error ? fraction.error : null;
		return reject('type', '"' + raw + '" is not a valid ' + describeMeasure(measure, options) + (reason ? ': ' + reason : ''), reason);
	}

	if (terms.length === 1) {
		const term = terms[0];
		if (!term.u) {
			const unit = defaultUnitFor(measure, options);
			if (!unit) return reject('invalid_unit', 'no default unit for "' + raw + '"');
			return { value: term.n, unit: unit.id };
		}
		const unit = resolveUnit(term.u, measureId);
		if (!unit) {
			return reject('invalid_unit', 'unit "' + term.u + '" is not a ' + describeMeasure(measure, options) + ' unit');
		}
		return { value: term.n, unit: unit.id };
	}

	// Two terms: both must name units that together form a composite.
	const first = terms[0];
	const second = terms[1];
	if (!first.u || !second.u) return reject('type', '"' + raw + '" is not a valid ' + describeMeasure(measure, options));
	const major = resolveUnit(first.u, measureId);
	const minor = resolveUnit(second.u, measureId);
	if (!major) return reject('invalid_unit', 'unit "' + first.u + '" is not a ' + describeMeasure(measure, options) + ' unit');
	if (!minor) return reject('invalid_unit', 'unit "' + second.u + '" is not a ' + describeMeasure(measure, options) + ' unit');
	const unit = findComposite(measure, major.id, minor.id);
	if (!unit) return reject('invalid_unit', '"' + major.id + '" and "' + minor.id + '" do not form a unit');
	const sign = first.n < 0 || text.charAt(0) === '-' ? -1 : 1;
	return { value: sign * (Math.abs(first.n) + second.n / unit.minorPerMajor), unit: unit.id };
}

// The number of a { value, unit } object: a number, or text that is one ("2",
// "1 1/2"). { value } | a refusal.
function objectValue(raw) {
	let value = raw.value;
	if (typeof raw.value === 'string' && raw.value.trim() !== '') {
		// "1 1/2" as the value of { value, unit } reads as the fraction.
		const fraction = Fraction.readFraction(raw.value);
		if (fraction && fraction.error) return reject('type', 'a measurement needs a numeric value: "' + raw.value + '": ' + fraction.error, fraction.error);
		value = fraction ? fraction.value : Number(raw.value);
	}
	if (typeof value !== 'number' || !Number.isFinite(value)) {
		return reject('type', 'a measurement needs a numeric value');
	}
	return { value };
}

function parseObject(raw, measure, options) {
	const read = objectValue(raw);
	if (read.error) return read;
	const value = read.value;
	let unit;
	if (raw.unit === undefined || raw.unit === null || raw.unit === '') {
		unit = defaultUnitFor(measure, options);
		if (!unit) return reject('invalid_unit', 'a measurement needs a unit');
	} else {
		if (typeof raw.unit !== 'string') return reject('invalid_unit', 'a measurement unit must be a string');
		unit = resolveUnit(raw.unit, measure ? measure.id : undefined);
		if (!unit) return reject('invalid_unit', 'unit "' + raw.unit + '" is not a ' + describeMeasure(measure, options) + ' unit');
	}
	return { value, unit: unit.id };
}

/**
 * Like parseMeasurement but says WHY it failed:
 *   { value, unit } | { error: 'type' | 'invalid_unit', message }
 */
function parseMeasurementDetailed(input, options) {
	const opts = options || {};
	// The options one own unit's value is read by (registry.js scopeOptions).
	if (isOwnUnitId(opts.measure)) return parseOneOwn(input, opts);
	// The element's own units are read FIRST (KPR-1109): a name its author
	// gave keeps its meaning whatever the registry comes to spell.
	const owns = ownUnitsOf(opts);
	if (owns.length) {
		const own = parseOwn(input, opts);
		if (own) return own;
	}
	const measures = allowedMeasures(opts).filter(id => !isOwnUnitId(id));
	if (measures.length > 1 || owns.length) return parseSeveral(input, opts, measures, owns);
	return parseIn(input, opts);
}

// ------------------------------------------------------------------ own units

/**
 * The reading of an input that names one of the element's OWN units
 * (KPR-1109): text whose first term is a number and whose rest is the unit's
 * singular or plural, in any case ("2 smidgens", "1 1/2 Smidgen", "1/2
 * smidgen"), or an object whose unit is the unit's id or one of its names.
 * Null when the input names none of them -- the registry reads it then. A
 * negative number reads; the normaliser refuses it (an own unit counts).
 */
function parseOwn(input, opts) {
	if (typeof input === 'string') {
		const text = input.replace(GROUPING_COMMA_RE, '').trim();
		if (!text) return null;
		const number = readTermNumber(text, true);
		if (!number || number.error) return null;
		const unitText = text.slice(number.length).trim();
		if (!unitText) return null;
		const own = resolveOwnUnit(unitText, opts);
		return own ? { value: number.n, unit: own.id } : null;
	}
	if (input && typeof input === 'object' && !Array.isArray(input) && typeof input.unit === 'string') {
		const own = resolveOwnUnit(input.unit, opts);
		if (!own) return null;
		const read = objectValue(input);
		return read.error ? read : { value: read.value, unit: own.id };
	}
	return null;
}

/**
 * The reading under the options of ONE own unit (registry.js scopeOptions):
 * that unit is the default, so a bare number, a number in text, or an object
 * with no unit is in it; any other unit is refused.
 */
function parseOneOwn(input, opts) {
	const own = ownUnitOf(opts.measure, opts);
	if (!own) return reject('type', 'unknown measure "' + opts.measure + '"');
	if (input === undefined || input === null) return reject('type', 'a measurement is required');
	if (typeof input === 'number') {
		if (!Number.isFinite(input)) return reject('type', 'a measurement must be a finite number');
		return { value: input, unit: own.id };
	}
	const named = parseOwn(input, opts);
	if (named) return named;
	if (typeof input === 'string') {
		const text = input.replace(GROUPING_COMMA_RE, '').trim();
		const number = text ? readTermNumber(text, true) : null;
		if (number && !number.error && number.length === text.length) return { value: number.n, unit: own.id };
		return reject('invalid_unit', '"' + input + '" is not a number of ' + own.plural);
	}
	if (typeof input === 'object' && !Array.isArray(input)) {
		if (input.unit === undefined || input.unit === null || input.unit === '') {
			const read = objectValue(input);
			return read.error ? read : { value: read.value, unit: own.id };
		}
		return reject('invalid_unit', 'unit "' + String(input.unit) + '" is not ' + own.plural);
	}
	return reject('type', 'a measurement must be a number, a string or a { value, unit } object');
}

/**
 * The reading for an element that allows SEVERAL measures (KPR-1085,
 * registry.js allowedMeasures): the single-measure reading below, made in
 * each measure the element allows, in its order. The primary comes first,
 * with its default unit, so a number with no unit means that unit exactly as
 * it always did. Every other measure reads only text or an object that spells
 * one of ITS units: no default unit stands in for one. A spelling names one
 * unit in the whole registry, so at most one measure reads a spelled unit,
 * and the order decides nothing but the number with none. When no measure
 * reads it, the PRIMARY's refusal is the answer, naming every measure the
 * element allows ("unit "kn" is not a volume, mass or count unit").
 */
function parseSeveral(input, opts, measures, owns) {
	const label = measures.length > 1 ? measures.slice(0, -1).join(', ') + ' or ' + measures[measures.length - 1] : measures[0];
	let refusal = null;
	for (let i = 0; i < measures.length; i++) {
		const scoped = Object.assign({}, scopeOptions(opts, measures[i], null));
		if (i > 0) NO_DEFAULT.add(scoped);
		LABELS.set(scoped, label);
		const result = parseIn(input, scoped);
		if (!result.error) return result;
		if (!refusal) refusal = result;
	}
	// An element with own units (KPR-1109; parseOwn read them first): a unit
	// that is none of its measures' is none of its own either, and says so.
	if (refusal && refusal.error === 'invalid_unit' && owns && owns.length && / unit$/.test(refusal.message)) {
		const named = refusal.message.match(/^unit "(.*)" is not /);
		const own = named ? resolveOwnUnit(named[1], opts) : null;
		const message = own
			? '"' + String(typeof input === 'string' ? input : named[1]) + '" is not a valid measurement: ' + own.singular + ' is counted in one number, like "2 ' + own.plural + '"'
			: refusal.message + ' or one of its own units (' + owns.map(entry => entry.singular).join(', ') + ')';
		refusal = Object.assign({}, refusal, { message });
	}
	return refusal;
}

/** The single-measure reading (the whole of it before KPR-1085). */
function parseIn(input, opts) {
	const measure = opts.measure ? getMeasure(opts.measure) : null;
	if (opts.measure && !measure) return reject('type', 'unknown measure "' + opts.measure + '"');

	if (input === undefined || input === null) return reject('type', 'a measurement is required');
	if (typeof input === 'number') {
		if (!Number.isFinite(input)) return reject('type', 'a measurement must be a finite number');
		const unit = defaultUnitFor(measure, opts);
		if (!unit) return reject('invalid_unit', 'no default unit for ' + input);
		return { value: input, unit: unit.id };
	}
	if (typeof input === 'string') return parseString(input, measure, opts);
	if (typeof input === 'object' && !Array.isArray(input)) return parseObject(input, measure, opts);
	return reject('type', 'a measurement must be a number, a string or a { value, unit } object');
}

/**
 * parseMeasurement(input, { measure, measures, ownUnits, defaultUnit, units }) -> { value, unit } | null.
 * Never throws. Composites come back as their decimal magnitude:
 * "8 lb 7 oz" -> { value: 8.4375, unit: 'lb-oz' }.
 */
function parseMeasurement(input, options) {
	try {
		const result = parseMeasurementDetailed(input, options);
		return result && result.error ? null : result;
	} catch (e) {
		return null;
	}
}

module.exports = {
	parseMeasurement,
	parseMeasurementDetailed,
};
